import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, ChevronDown, ChevronRight, MapPin, X, Check } from 'lucide-react';
import { clientService } from '../services/api';
import { useDialog } from '../context/DialogContext';

function Clients() {
  const { toast, confirm } = useDialog();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  // New client form
  const [newClientName, setNewClientName] = useState('');
  const [addingClient, setAddingClient] = useState(false);

  // Edit client inline
  const [editingClientId, setEditingClientId] = useState(null);
  const [editingClientName, setEditingClientName] = useState('');

  // Add location form per client
  const [addingLocationFor, setAddingLocationFor] = useState(null);
  const [newLocation, setNewLocation] = useState({ label: '', address: '', city: '', country: '' });

  // Edit location inline
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [editingLocation, setEditingLocation] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const data = await clientService.getClients();
      setClients(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) return;
    try {
      await clientService.createClient({ name: newClientName.trim() });
      setNewClientName('');
      setAddingClient(false);
      load();
    } catch (err) { toast('Error creating client', 'error'); }
  };

  const handleUpdateClient = async (id) => {
    try {
      await clientService.updateClient(id, { name: editingClientName });
      setEditingClientId(null);
      load();
    } catch (err) { toast('Error updating client', 'error'); }
  };

  const handleDeleteClient = async (id) => {
    if (!await confirm({ title: 'Delete this client?', message: 'All its locations will be deleted too.', confirmLabel: 'Delete', destructive: true })) return;
    try {
      await clientService.deleteClient(id);
      load();
    } catch (err) { toast('Error deleting client', 'error'); }
  };

  const handleAddLocation = async (clientId) => {
    try {
      await clientService.addLocation(clientId, newLocation);
      setAddingLocationFor(null);
      setNewLocation({ label: '', address: '', city: '', country: '' });
      load();
    } catch (err) { toast('Error adding location', 'error'); }
  };

  const handleUpdateLocation = async (clientId, locationId) => {
    try {
      await clientService.updateLocation(clientId, locationId, editingLocation);
      setEditingLocationId(null);
      load();
    } catch (err) { toast('Error updating location', 'error'); }
  };

  const handleDeleteLocation = async (clientId, locationId) => {
    if (!await confirm({ title: 'Delete this location?', confirmLabel: 'Delete', destructive: true })) return;
    try {
      await clientService.deleteLocation(clientId, locationId);
      load();
    } catch (err) { toast('Error deleting location', 'error'); }
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900"><span className="hidden md:inline">Clients</span><span className="md:hidden text-base font-normal text-gray-500">Clients and their facilities</span></h1>
        <button
          onClick={() => setAddingClient(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Client
        </button>
      </div>

      {/* New client form */}
      {addingClient && (
        <div className="bg-white shadow rounded-lg p-4 flex gap-2 items-center">
          <input
            autoFocus
            type="text"
            value={newClientName}
            onChange={e => setNewClientName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreateClient(); if (e.key === 'Escape') setAddingClient(false); }}
            placeholder="Client name"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <button onClick={handleCreateClient} className="p-2 text-green-600 hover:text-green-700">
            <Check className="w-5 h-5" />
          </button>
          <button onClick={() => setAddingClient(false)} className="p-2 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {clients.length === 0 && !addingClient && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No clients yet. Add your first client above.</p>
        </div>
      )}

      {/* Client list */}
      <div className="space-y-3">
        {clients.map(client => (
          <div key={client.id} className="bg-white shadow rounded-lg overflow-hidden">
            {/* Client header */}
            <div className="flex items-center gap-3 p-4">
              <button
                onClick={() => setExpanded(e => ({ ...e, [client.id]: !e[client.id] }))}
                className="-m-1 p-1 text-gray-400 hover:text-gray-600"
                aria-label={expanded[client.id] ? 'Collapse' : 'Expand'}
              >
                {expanded[client.id]
                  ? <ChevronDown className="w-5 h-5" />
                  : <ChevronRight className="w-5 h-5" />}
              </button>

              {editingClientId === client.id ? (
                <>
                  <input
                    autoFocus
                    type="text"
                    value={editingClientName}
                    onChange={e => setEditingClientName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleUpdateClient(client.id); if (e.key === 'Escape') setEditingClientId(null); }}
                    className="flex-1 px-2 py-1 border border-blue-300 rounded focus:outline-none"
                  />
                  <button onClick={() => handleUpdateClient(client.id)} className="p-2 md:p-1 text-green-600"><Check className="w-5 h-5 md:w-4 md:h-4" /></button>
                  <button onClick={() => setEditingClientId(null)} className="p-2 md:p-1 text-gray-400"><X className="w-5 h-5 md:w-4 md:h-4" /></button>
                </>
              ) : (
                <>
                  <span className="flex-1 font-semibold text-gray-900">{client.name}</span>
                  <span className="text-xs text-gray-400 mr-2">{client.locations.length} location{client.locations.length !== 1 ? 's' : ''}</span>
                  <button
                    onClick={() => { setEditingClientId(client.id); setEditingClientName(client.name); }}
                    className="p-2 md:p-1 text-gray-400 hover:text-blue-600"
                    aria-label="Rename client"
                  ><Edit2 className="w-5 h-5 md:w-4 md:h-4" /></button>
                  <button onClick={() => handleDeleteClient(client.id)} className="p-2 md:p-1 text-gray-400 hover:text-red-600" aria-label="Delete client">
                    <Trash2 className="w-5 h-5 md:w-4 md:h-4" />
                  </button>
                </>
              )}
            </div>

            {/* Locations */}
            {expanded[client.id] && (
              <div className="border-t border-gray-100 px-4 pb-4">
                <div className="space-y-2 mt-3">
                  {client.locations.map(loc => (
                    <div key={loc.id} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      {editingLocationId === loc.id ? (
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {['label', 'address', 'city', 'country'].map(field => (
                            <input
                              key={field}
                              type="text"
                              placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                              value={editingLocation[field] || ''}
                              onChange={e => setEditingLocation(l => ({ ...l, [field]: e.target.value }))}
                              className="px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none"
                            />
                          ))}
                          <div className="sm:col-span-2 flex gap-2">
                            <button onClick={() => handleUpdateLocation(client.id, loc.id)} className="text-green-600 text-sm flex items-center gap-1"><Check className="w-3 h-3" /> Save</button>
                            <button onClick={() => setEditingLocationId(null)} className="text-gray-400 text-sm">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1">
                          {loc.label && <span className="font-medium text-sm text-blue-700 mr-2">{loc.label}</span>}
                          <span className="text-sm text-gray-700">
                            {[loc.address, loc.city, loc.country].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      )}
                      {editingLocationId !== loc.id && (
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            onClick={() => { setEditingLocationId(loc.id); setEditingLocation({ label: loc.label || '', address: loc.address || '', city: loc.city || '', country: loc.country || '' }); }}
                            className="p-2 md:p-1 text-gray-400 hover:text-blue-600"
                            aria-label="Edit location"
                          ><Edit2 className="w-4 h-4 md:w-3 md:h-3" /></button>
                          <button onClick={() => handleDeleteLocation(client.id, loc.id)} className="p-2 md:p-1 text-gray-400 hover:text-red-600" aria-label="Delete location">
                            <Trash2 className="w-4 h-4 md:w-3 md:h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add location form */}
                {addingLocationFor === client.id ? (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { key: 'label', placeholder: 'Label (e.g. KEM)' },
                      { key: 'address', placeholder: 'Address' },
                      { key: 'city', placeholder: 'City' },
                      { key: 'country', placeholder: 'Country' },
                    ].map(({ key, placeholder }) => (
                      <input
                        key={key}
                        type="text"
                        placeholder={placeholder}
                        value={newLocation[key]}
                        onChange={e => setNewLocation(l => ({ ...l, [key]: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    ))}
                    <div className="sm:col-span-2 flex gap-2">
                      <button onClick={() => handleAddLocation(client.id)} className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">Add</button>
                      <button onClick={() => setAddingLocationFor(null)} className="px-3 py-1 text-gray-600 text-sm">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => { setAddingLocationFor(client.id); setExpanded(e => ({ ...e, [client.id]: true })); }}
                    className="mt-3 inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add location
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Clients;
